import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../../components/Config/api.service';
import { resoudreIdentite } from '../../components/Auth/identite';
import { memoriserIdentite } from '../../components/Auth/permissions';
import LanguageSwitcher from '../../components/Header/LanguageSwitcher';
import { signalerErreur } from '../../components/Utils/signalerErreur';

const Login = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const requestedPath = location.state?.from
        ? `${location.state.from.pathname || '/'}${location.state.from.search || ''}${location.state.from.hash || ''}`
        : '/';
    const savedCode = typeof window !== 'undefined' ? localStorage.getItem('selectedCountryCode') : null;
    const savedLabel = typeof window !== 'undefined' ? localStorage.getItem('selectedCountryLabel') : null;

    const [country, setCountry] = useState(savedCode || '');
    const [countryLabel, setCountryLabel] = useState(savedLabel || '');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const authMessage = sessionStorage.getItem('authMessage');
        if (authMessage) {
            setErrorMessage(authMessage);
            sessionStorage.removeItem('authMessage');
        }
        const authSuccessMessage = sessionStorage.getItem('authSuccessMessage');
        if (authSuccessMessage) {
            setSuccessMessage(authSuccessMessage);
            sessionStorage.removeItem('authSuccessMessage');
        }
    }, []);

    useEffect(() => {
        const c = localStorage.getItem('selectedCountryCode');
        const l = localStorage.getItem('selectedCountryLabel');
        if (!c) {
            // redirect to country selection page if no country chosen
            navigate('/countryselect', { replace: true, state: location.state });
            return;
        }
        setCountry(c);
        if (l) setCountryLabel(l);
    }, [location.state, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');
        try {
            localStorage.removeItem('user');
            await apiService.authenticate(email, password, country);
            // JWT now lives in HttpOnly cookies set by the proxy; we no longer
            // store access_token / refresh_token in localStorage.
            localStorage.setItem('isConnected', true);

            // Chemin commun avec App.js : C2 d'abord, `/user` en repli.
            // Interroger `/user` directement était refusé par l'allowlist
            // employé du proxy, et l'intercepteur d'api.service redirige sur
            // /403 dès qu'un 403 arrive — l'employé voyait la page 403 passer
            // avant son espace.
            const utilisateur = await resoudreIdentite();
            if (utilisateur) {
                memoriserIdentite(utilisateur);
            }

            window.location.href = requestedPath;
        } catch (err) {
            signalerErreur(err, 'Login.handleSubmit');
            setLoading(false);
            setErrorMessage(t('login.defaultError'));
        }
    };

    return (
        <div className="w-full flex items-center justify-center p-8 min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
            <div className="w-full max-w-md">
                <div className="bg-white shadow-lg rounded-xl p-8">
                    <div className="flex items-center justify-between mb-4">
                        <a href="/" className="text-4xl text-[--color-primary] font-extrabold inline-flex items-center">
                            YROS
                        </a>
                        <LanguageSwitcher />
                    </div>

                    <div className="flex items-center mb-4">
                        <button
                            type="button"
                            onClick={() => navigate('/countryselect')}
                            className="mr-2 text-gray-400 hover:text-[--color-primary] focus:outline-none"
                            title={t('login.changeCountryTitle')}
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <span className="text-lg font-semibold text-gray-700">
                            {countryLabel || country?.toUpperCase()}
                        </span>
                    </div>

                    {/* <div className="mb-4 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded">
                        <strong>Important :</strong> Le pays sélectionné détermine la base de données et la configuration.
                    </div> */}

                    <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('login.heading')}</h2>
                    <p className="text-gray-600 mb-6">{t('login.subheading')}</p>

                    {errorMessage && (
                        <div data-testid="login-erreur" className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded flex items-center">
                            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                            </svg>
                            {errorMessage}
                        </div>
                    )}

                    {successMessage && (
                        <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded flex items-center">
                            {successMessage}
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <div className="relative w-16 h-16">
                                <div className="absolute top-0 w-full h-full border-4 border-gray-200 rounded-full"></div>
                                <div className="absolute top-0 w-full h-full border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
                            </div>
                        </div>
                    ) : (
                        <form data-testid="login-formulaire" onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="formEmail" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('login.emailLabel')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                                        </svg>
                                    </div>
                                    <input
                                        type="email"
                                        id="formEmail"
                                        data-testid="login-email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all"
                                        placeholder={t('login.emailPlaceholder')}
                                    />
                                </div>
                            </div>
                            <div>
                                <label htmlFor="formPassword" className="block text-sm font-medium text-gray-700 mb-2">
                                    {t('login.passwordLabel')}
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        id="formPassword"
                                        data-testid="login-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all"
                                        placeholder="••••••••••"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                        >
                                            {showPassword ? t('login.hidePassword') : t('login.showPassword')}
                                        </button>
                                    </div>
                                </div>
                                <div className="mt-2 text-right">
                                    <Link to="/mot-de-passe-oublie" state={{ login: email }} className="text-sm text-[--color-primary] hover:underline">
                                        {t('login.forgotPassword')}
                                    </Link>
                                </div>
                            </div>

                            <div className="flex flex-col space-y-4">
                                <button
                                    type="submit"
                                    className="w-full bg-[--color-primary] hover:bg-[--color-orange] text-white py-3 px-4 rounded-lg font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    {t('login.submitButton')}
                                </button>

                                <p className="text-sm text-gray-500 text-center">
                                    {t('login.noAccountText')}
                                </p>
                            </div>
                        </form>
                    )}
                </div>
                <div className="mt-6 text-center text-sm text-gray-500">
                    {t('login.termsPrefix')}{' '}
                    <a href="#" className="text-[--color-primary] hover:underline">{t('login.termsLink')}</a> {t('login.termsAnd')}{' '}
                    <a href="#" className="text-[--color-primary] hover:underline">{t('login.privacyLink')}</a>.
                </div>
            </div>
        </div>
    );
};

export default Login;
